import React, { useState, useEffect } from 'react';
import { Box, TextField, MenuItem, Button, Autocomplete, Typography, Stack } from '@mui/material';
import { fetchApi } from '../hooks/useApi';
import { isAuthenticated, getUserEmail } from '../auth';
import { validateWeight, validateHeight, getValidationRanges, getAgeGroup } from '../utils/validation';

/**
 * Calculate BMI and determine category. Preserves the original calculation logic.
 *
 * @param {number} weight - Weight in kg
 * @param {number} height - Height in cm
 * @returns {Object} { bmi, category }
 */
function calculateBMI(weight, height) {
  const heightInMeters = height / 100;
  const bmi = weight / (heightInMeters * heightInMeters);

  let category;
  if (bmi < 18.5) category = 'Underweight';
  else if (bmi < 25) category = 'Normal weight';
  else if (bmi < 30) category = 'Overweight';
  else category = 'Obese';

  return { bmi: parseFloat(bmi.toFixed(2)), category };
}

const YEAR_OPTIONS = Array.from(
  { length: new Date().getFullYear() - 1919 },
  (_, i) => String(new Date().getFullYear() - i)
);

/**
 * BMIForm - collects weight/height (and year of birth / sex) and submits a BMI
 * calculation. When signed in, the userId (email) comes from the ID token and is
 * not asked for.
 *
 * @param {Object} props
 * @param {Function} props.onResult - onResult(bmi, category, userId)
 * @param {Function} props.onError
 */
function BMIForm({ onResult, onError }) {
  // userId IS the Cognito email; when signed in take it from the token.
  const authedEmail = isAuthenticated() ? getUserEmail() : null;
  const [userId, setUserId] = useState(authedEmail || '');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [yearOfBirth, setYearOfBirth] = useState('');
  const [sex, setSex] = useState('');
  const [loading, setLoading] = useState(false);
  const [weightError, setWeightError] = useState('');
  const [heightError, setHeightError] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(false);

  const loadUserProfile = async (id) => {
    if (!id) return;
    setLoadingProfile(true);
    try {
      const data = await fetchApi(`/user/${encodeURIComponent(id)}/tier`);
      if (data.yearOfBirth) {
        handleYearOfBirthChange(String(data.yearOfBirth));
      }
      if (data.sex) {
        setSex(data.sex);
      }
    } catch {
      // User not found or error — let the user enter manually.
    } finally {
      setLoadingProfile(false);
    }
  };

  // Signed in: prefill year/sex from the stored profile for the known email.
  useEffect(() => {
    if (authedEmail) {
      loadUserProfile(authedEmail);
    }
  }, []);

  const yob = yearOfBirth ? parseInt(yearOfBirth) : null;

  const handleWeightChange = (value) => {
    setWeight(value);
    setWeightError(validateWeight(value, yob) || '');
  };

  const handleHeightChange = (value) => {
    setHeight(value);
    setHeightError(validateHeight(value, yob) || '');
  };

  const handleYearOfBirthChange = (value) => {
    setYearOfBirth(value);
    const parsed = value ? parseInt(value) : null;
    setWeightError(validateWeight(weight, parsed) || '');
    setHeightError(validateHeight(height, parsed) || '');
  };

  const ranges = getValidationRanges(yob);
  const isPediatric = yob && getAgeGroup(yob) !== 'adult';

  const handleSubmit = async (e) => {
    e.preventDefault();

    const wErr = validateWeight(weight, yob);
    const hErr = validateHeight(height, yob);
    if (wErr || hErr) {
      setWeightError(wErr || '');
      setHeightError(hErr || '');
      return;
    }

    setLoading(true);

    const weightNum = parseFloat(weight);
    const heightNum = parseFloat(height);

    try {
      const { bmi, category } = calculateBMI(weightNum, heightNum);

      const requestBody = { userId, weight: weightNum, height: heightNum };
      if (yearOfBirth) {
        requestBody.yearOfBirth = parseInt(yearOfBirth);
      }
      if (sex) {
        requestBody.sex = sex;
      }

      await fetchApi('/bmi', { method: 'POST', body: requestBody });

      setWeight('');
      setHeight('');
      onResult(bmi, category, userId);
    } catch (error) {
      const details = error.details;
      const msg = details && details.length
        ? 'Validation failed: ' + details.join('; ')
        : error.message;
      onError('Error calculating BMI: ' + msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" id="bmiForm" onSubmit={handleSubmit}>
      <Stack spacing={2.5}>
        {authedEmail ? (
          <Typography color="text.secondary" data-testid="bmi-form-userId">
            Email: <strong>{authedEmail}</strong>
          </Typography>
        ) : (
          <TextField
            type="email"
            id="userId"
            label="Email"
            required
            fullWidth
            placeholder="Enter your email address"
            value={userId}
            onChange={(e) => {
              setUserId(e.target.value);
              setYearOfBirth('');
              setSex('');
              setWeightError('');
              setHeightError('');
            }}
            onBlur={(e) => loadUserProfile(e.target.value)}
            inputProps={{ 'data-testid': 'bmi-form-userId' }}
          />
        )}

        <Autocomplete
          freeSolo
          options={YEAR_OPTIONS}
          value={yearOfBirth || null}
          disabled={loadingProfile}
          // onInputChange fires for BOTH typing and option-selection in freeSolo
          // mode, so it alone covers every path — a second onChange handler would
          // just run the same validation twice per selection.
          onInputChange={(_, v) => handleYearOfBirthChange((v || '').replace(/\D/g, '').slice(0, 4))}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Year of Birth"
              placeholder={loadingProfile ? 'Loading…' : 'Type or select year'}
              inputProps={{ ...params.inputProps, inputMode: 'numeric', 'data-testid': 'bmi-form-yearOfBirth' }}
            />
          )}
        />

        {isPediatric && (
          <TextField
            select
            id="sex"
            label="Sex (for pediatric BMI)"
            fullWidth
            value={sex}
            onChange={(e) => setSex(e.target.value)}
            inputProps={{ 'data-testid': 'bmi-form-sex' }}
          >
            <MenuItem value="">-- Select --</MenuItem>
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
          </TextField>
        )}

        <TextField
          type="number"
          id="weight"
          label="Weight (kg)"
          required
          fullWidth
          placeholder="70"
          value={weight}
          onChange={(e) => handleWeightChange(e.target.value)}
          error={!!weightError}
          helperText={weightError || ''}
          inputProps={{ step: '0.1', min: ranges.weight_min, max: ranges.weight_max, 'data-testid': 'bmi-form-weight' }}
        />

        <TextField
          type="number"
          id="height"
          label="Height (cm)"
          required
          fullWidth
          placeholder="175"
          value={height}
          onChange={(e) => handleHeightChange(e.target.value)}
          error={!!heightError}
          helperText={heightError || ''}
          inputProps={{ step: '0.1', min: ranges.height_min, max: ranges.height_max, 'data-testid': 'bmi-form-height' }}
        />

        <Button type="submit" size="large" disabled={loading} id="calculateBtn" data-testid="bmi-form-submit">
          {loading ? 'Calculating…' : 'Calculate BMI'}
        </Button>
      </Stack>
    </Box>
  );
}

export { calculateBMI };
export default BMIForm;
